import React, { useEffect, useState } from 'react';
import { useUpdateAccountMutation } from '../endpoints/accounts.endpoints';
import { Account } from '../types/client-management.types';
import { CircleAlert } from 'lucide-react';
import CompanySelector from './company-selector';
import { Company } from 'src/pages/billing/types/billing.types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from 'src/components/ui/table';

interface AccountTableProps {
  accounts: Account[];
  companies: Company[];
  filter?: 'attention_needed' | 'all';
  className?: string;
  searchQuery: string;
  onCompanySelect?: (accountId: string, companyId: string) => void;
}

const AccountTable: React.FC<AccountTableProps> = ({
  className,
  filter = 'all',
  accounts,
  companies,
  searchQuery,
  onCompanySelect,
}) => {
  const [filteredAccounts, setFilteredAccounts] = useState<Account[]>([]);

  const [updateAccount, { isLoading: isUpdating }] = useUpdateAccountMutation();

  useEffect(() => {
    setFilteredAccounts(accounts.filter((account: Account) => {
      if (filter === 'attention_needed') {
        if (searchQuery.trim()) {
          return account.attention_needed && (account.title.toLowerCase().includes(searchQuery.toLowerCase()) || account.source.toLowerCase().includes(searchQuery.toLowerCase()));
        }
        return account.attention_needed;
      }

      if (searchQuery.trim()) {
        return account.title.toLowerCase().includes(searchQuery.toLowerCase()) || account.source.toLowerCase().includes(searchQuery.toLowerCase());
      }

      return true;
    }));
  }, [filter, accounts, searchQuery]);

  const handleCompanySelect = async(accountId: string, companyId: string) => {
    try {
      await updateAccount({
        accountId,
        company: companyId || null,
      }).unwrap();
      onCompanySelect?.(accountId, companyId);
    } catch (error) {
      console.error('Failed to update company:', error);
    }
  };

  return (
    <div className={`overflow-y-auto w-full ${className} bg-white rounded-lg`}>
      <Table className={isUpdating ? 'opacity-50 pointer-events-none' : ''}>
        <TableHeader>
          <TableRow className="border-b border-gray-200">
            <TableHead className="w-10 text-gray-600"> </TableHead>
            <TableHead className="text-gray-600">Title</TableHead>
            <TableHead className="text-gray-600">Source</TableHead>
            <TableHead className="text-gray-600 min-w-[200px]">Company</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {filteredAccounts.map((account: Account) => (
            <TableRow key={account.id} className="border-b border-gray-100 align-top">
              <TableCell className="py-3">
                {account.attention_needed ? (
                  <span className="text-red-500" title="Attention needed">
                    <CircleAlert className="size-4" />
                  </span>
                ) : null}
              </TableCell>
              <TableCell className="py-3 font-medium text-gray-900">
                {account.title}
              </TableCell>
              <TableCell className="py-3 text-sm text-gray-600">
                {account.source.toLocaleLowerCase().replace('_', ' ')}
              </TableCell>
              <TableCell className="py-2">
                <CompanySelector
                  accountId={account.id}
                  companies={companies}
                  companyId={account.company || ''}
                  accountName={account.title}
                  campaignName={account.ext_account_id}
                  attentionNeeded={account.attention_needed}
                  onCompanySelect={(companyId) => handleCompanySelect(account.id, companyId)}
                />
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  );
};

export default AccountTable;
